import { useEffect, useRef, useState } from "react";

import { addBridgeListener, requestElationRefresh } from "./extensionBridge";
import type { ElationClinical, ElationDataPayload, ElationPatient, ExtensionMessage } from "./types";
import { isEmbeddedInExtension } from "./types";

interface ElationBridgeReturn {
  data: ElationPatient | null;
  status: ElationDataPayload["status"];
  clinical: ElationClinical | null;
  /** Whether the scraped Elation tab is the active/foreground tab. */
  sourceTabActive: boolean;
}

/**
 * Receives Elation scraper data from the extension via postMessage.
 * Only useful in embedded mode — returns disconnected defaults in standalone.
 */
export function useElationBridge(): ElationBridgeReturn {
  const [data, setData] = useState<ElationPatient | null>(null);
  const [status, setStatus] = useState<ElationDataPayload["status"]>("disconnected");
  const [clinical, setClinical] = useState<ElationClinical | null>(null);
  const [sourceTabActive, setSourceTabActive] = useState(false);

  useEffect(() => {
    return addBridgeListener((msg: ExtensionMessage) => {
      if (msg.type === "ELATION_DATA") {
        const payload = msg.payload as ElationDataPayload;
        setData(payload.data);
        setStatus(payload.status);
        setClinical(payload.clinical ?? null);
        setSourceTabActive(payload.sourceTabActive ?? false);
      }
    });
  }, []);

  // The sidepanel only pushes on change and its first scrape can land before
  // this hook mounts (or return nothing while the Elation tab is still
  // loading) — so passively waiting can leave the panel blank until the 10s
  // poll. Actively pull on mount and retry through the cold-start window;
  // stop once connected or after a few attempts.
  const statusRef = useRef(status);
  statusRef.current = status;
  useEffect(() => {
    if (!isEmbeddedInExtension()) return;
    requestElationRefresh();
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      // Stop once the sidepanel reports a definitive status — retrying can't
      // help when Elation is connected, absent, errored, or permission-gated
      // (a chart opened later is picked up by useElationData's tab listeners).
      const s = statusRef.current;
      const settled =
        s === "connected" ||
        s === "no-tab" ||
        s === "error" ||
        s === "permission-needed";
      if (settled || tries >= 6) {
        clearInterval(id);
        return;
      }
      requestElationRefresh();
    }, 1500);
    return () => clearInterval(id);
  }, []);

  return { data, status, clinical, sourceTabActive };
}

/**
 * The FOREGROUND chart's EHR patient id, or null when no chart is in front.
 *
 * A deliberately narrow sibling of `useElationBridge` for consumers that only
 * need to know WHICH chart the clinician is looking at — today the app shell,
 * which uses it to step off the Home landing page so an in-context patient can
 * auto-navigate (see `shouldLeaveHomeForChart`).
 *
 * WHY NOT just call `useElationBridge`: while a note is open the extension
 * re-sends ELATION_DATA roughly every 300ms (the content script's live note
 * push and the sidepanel's per-section re-read both run at that gap — see
 * LIVE_SECTION_REFRESH_MS). `useElationBridge` stores the payload OBJECTS, so
 * every one of those pushes is a fresh identity and re-renders its consumer
 * ~3x/second. That is an accepted cost inside the pages that actually display
 * the note; in the SHELL it would cascade into every mounted keep-alive pane
 * (Home, Worklist, Schedule, the open profile) on the same cadence.
 *
 * Holding a STRING instead makes React's `useState` bail out on an unchanged
 * value, so a shell subscribed to this re-renders only when the clinician
 * genuinely moves to a different chart — a handful of times an hour, not three
 * times a second.
 *
 * Not EHR-specific despite the file name: the bridge carries whichever adapter
 * scraped the chart (Elation, eCW, athenahealth, Office Ally — see
 * extension/content/adapters), and `ehrPatientId` is each adapter's own
 * in-context patient id.
 */
export function useForegroundChartPatientId(): string | null {
  const [patientId, setPatientId] = useState<string | null>(null);

  useEffect(() => {
    return addBridgeListener((msg: ExtensionMessage) => {
      if (msg.type !== "ELATION_DATA") return;
      const payload = msg.payload as ElationDataPayload;
      // A backgrounded chart is not "in context" — auto-navigation has always
      // required the chart to be the tab the clinician is actually viewing.
      const next = payload.sourceTabActive
        ? (payload.data?.ehrPatientId ?? null)
        : null;
      // Same id → same string → React bails out and nothing re-renders.
      setPatientId(next);
    });
  }, []);

  // Self-sufficient cold start: the sidepanel pushes only on CHANGE, so a
  // freshly-mounted listener can otherwise sit blank until something moves.
  // One request on mount, no retry loop — the cold-start retries belong to
  // `useElationBridge` on the search page, and its answer is broadcast to
  // every listener in this frame, including this one.
  useEffect(() => {
    if (!isEmbeddedInExtension()) return;
    requestElationRefresh();
  }, []);

  return patientId;
}
